import i18n from '@/i18n';
import { ApiError } from './client';

/** Known domain error codes mapped to their i18n message key. */
const CODE_KEYS: Record<string, string> = {
  'organization.last_owner': 'errors.organization.last_owner',
  'membership.exists': 'errors.membership.exists',
  no_access: 'errors.no_access',
  'boundary.bad_geometry': 'errors.boundary.bad_geometry',
  'boundary.invalid_geometry': 'errors.boundary.invalid_geometry',
  'boundary.copied_from_mismatch': 'errors.boundary.copied_from_mismatch',
  'boundary.staff_only': 'errors.boundary.staff_only',
  'project.not_active': 'errors.project.not_active',
  'data_object.kind_not_uploadable': 'errors.data_object.kind_not_uploadable',
  'data_object.replaces_mismatch': 'errors.data_object.replaces_mismatch',
  'data_object.bad_uri': 'errors.data_object.bad_uri',
  'data_object.uri_not_found': 'errors.data_object.uri_not_found',
  'upload.bad_name': 'errors.upload.bad_name',
  'delete.not_allowed': 'errors.delete.not_allowed',
  'delete.raw_is_effective_data': 'errors.delete.raw_is_effective_data',
  'delete.raw_not_owned': 'errors.delete.raw_not_owned',
  'share.invalid_role': 'errors.share.invalid_role',
  'object.not_empty': 'errors.object.not_empty',
  'analysis.not_empty': 'errors.analysis.not_empty',
  'rename.not_allowed': 'errors.rename.not_allowed',
  'object.name_blank': 'errors.object.name_blank',
  'object.name_too_long': 'errors.object.name_too_long',
  'object.notes_too_long': 'errors.object.notes_too_long',
  'comment.empty': 'errors.comment.empty',
  'comment.too_long': 'errors.comment.too_long',
  'comment.not_author': 'errors.comment.not_author',
  'notes.moved_to_comments': 'errors.notes.moved_to_comments',
  'data_object.uri_unreadable': 'errors.data_object.uri_unreadable',
  'data_object.bad_crs': 'errors.data_object.bad_crs',
  'referenced.mark_ready_with_processed': 'errors.referenced.mark_ready_with_processed',
  'referenced.analysis_not_in_project': 'errors.referenced.analysis_not_in_project',
  'object.preprocess_available': 'errors.object.preprocess_available',
  'analysis.no_outputs': 'errors.analysis.no_outputs',
  'analysis.object_not_ready': 'errors.analysis.object_not_ready',
  'analysis.output_not_referenced': 'errors.analysis.output_not_referenced',
  'analysis.output_taken': 'errors.analysis.output_taken',
  'analysis.output_is_input': 'errors.analysis.output_is_input',
  'analysis.output_kind': 'errors.analysis.output_kind',
  'analysis.outputs_incomplete': 'errors.analysis.outputs_incomplete',
  'analysis.extra_not_deliverable': 'errors.analysis.extra_not_deliverable',
  'analysis.attach_after_runs': 'errors.analysis.attach_after_runs',
  'analysis.cancelled_attach': 'errors.analysis.cancelled_attach',
};

/**
 * Localized message for an API failure. Unmapped domain codes show the backend's own
 * message so the reason is never lost; the generic text is for non-domain failures.
 */
export function errorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return i18n.t('errors.generic');
  const key = CODE_KEYS[err.code];
  // `detail` is the backend's own message, for texts that name the object or kinds it is about.
  if (key) return i18n.t(key, { detail: err.message });
  if (err.code !== 'http_error' && err.message) return err.message;
  return i18n.t('errors.generic');
}

/** A refusal the backend attributes to one request field, to show at that field; null otherwise. */
export function fieldError(err: unknown): { field: string; message: string } | null {
  if (!(err instanceof ApiError) || !err.field) return null;
  // A serializer refusal carries no domain code; its own message is the reason.
  return { field: err.field, message: err.code === 'http_error' ? err.message : errorMessage(err) };
}
